import { AccordionProviderValue, ItemKey } from '@szhsin/react-accordion/types/utils/constants'
import { MutableRefObject, RefObject, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowRight } from '../ArrowRight.tsx'
import { FeedbackForm } from './FeedbackForm.tsx'
import GlobalContext from './GlobalContext.tsx'

type Category = 'quality' | 'impact'
const ClassificationButton = (props: {
  cat: Category
  rating: number
  text: string
  classification: { impact?: number; quality?: number }
  handleClassification: (cat: Category, rating: number) => void
}) => (
  <button
    className={`button button--small combined-button ${props.classification[props.cat] === props.rating && 'selected'}`}
    onClick={() => {
      props.handleClassification(props.cat, props.rating)
    }}
  >
    <svg xmlns="http://www.w3.org/2000/svg">
      <circle r="12" cx="15" cy="15" />
    </svg>
    <span dangerouslySetInnerHTML={{ __html: props.text }} />
  </button>
)
export const ExtendedFeedback = (props: {
  providerValue: AccordionProviderValue
  questionRef: MutableRefObject<null>
  feedbackRef: MutableRefObject<null>
  hintRef: MutableRefObject<null>
  requestId?: string | null
  userId?: string | null
  inputValue: string
}) => {
  const { t } = useTranslation()
  const [wasUseful, setwasUseful] = useState(false)
  const [wasNotUseful, setwasNotUseful] = useState(false)
  const { host, org } = useContext(GlobalContext)

  const [classification, setClassification] = useState<{ impact?: number; quality?: number }>({
    impact: undefined,
    quality: undefined,
  })
  const { toggle } = props.providerValue

  const setFocusOnQuestion = (questionRef: any) => {
    questionRef.current?.querySelector('textarea')?.focus()
    questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleClassification = async (cat: 'quality' | 'impact', rating: number) => {
    setClassification({ ...classification, [cat]: rating })
  }

  const sendClassification = async () => {
    try {
      const body: Record<string, string | number | undefined> = {
        ...classification,
        rID: props.requestId || '',
      }
      console.log(body)
      await fetch(`${host}/${org}/classification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      setwasUseful(true)
      setwasNotUseful(true)
    } catch (error) {
      console.log('Error sending feedback data:', error)
    }
  }

  const openAccordion = (accordion: ItemKey, inputRef: RefObject<HTMLTextAreaElement>) => {
    toggle(accordion, true)
    setTimeout(() => {
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 450)
  }
  return (
    <div className="box ">
      <div className={'classification'}>
        <div>
          <h4>Qualität</h4>
          <ClassificationButton
            handleClassification={handleClassification}
            text={'Gute Antwort, inhaltlich richtig'}
            cat={'quality'}
            classification={classification}
            rating={3}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={'Teils richtig, unvollständig'}
            cat={'quality'}
            classification={classification}
            rating={2}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={'Nicht korrekte Antwort'}
            cat={'quality'}
            classification={classification}
            rating={1}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={'Keine eigentliche Antwort, sollte aber eine liefern'}
            cat={'quality'}
            classification={classification}
            rating={4}
          />

          <ClassificationButton
            handleClassification={handleClassification}
            text={'Ich weiss es nicht'}
            cat={'quality'}
            classification={classification}
            rating={0}
          />
        </div>
        <div>
          <h4>Impact</h4>

          <ClassificationButton
            handleClassification={handleClassification}
            text={'Hier gibt es kein richtig oder falsch'}
            cat={'impact'}
            classification={classification}
            rating={1}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={'Nicht so wichtig'}
            cat={'impact'}
            classification={classification}
            rating={2}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={'Muss richtig sein'}
            classification={classification}
            cat={'impact'}
            rating={3}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={`Muss unbedingt richtig sein,<br/> hier kriegen wir sonst rechtliche Probleme`}
            cat={'impact'}
            classification={classification}
            rating={4}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={`Frage sollte gar nicht beantwortet werden`}
            cat={'impact'}
            classification={classification}
            rating={5}
          />
          <ClassificationButton
            handleClassification={handleClassification}
            text={'Ich weiss es nicht'}
            cat={'impact'}
            classification={classification}
            rating={0}
          />
        </div>
      </div>
      <button
        disabled={classification.quality === undefined && classification.impact === undefined}
        className={`button button--small combined-button `}
        onClick={() => sendClassification()}
      >
        <span>Abschicken</span>
      </button>

      <div className={`box__drawer ${wasUseful ? 'box__drawer--is-open' : ''}`}>
        <div className="box__drawer__content">{t('answers.thanksForFeedback')}</div>
      </div>
      <div className={`box__drawer ${wasNotUseful ? 'box__drawer--is-open' : ''}`}>
        <div className="box__drawer__content">
          <button className="button button--bare" onClick={() => setFocusOnQuestion(props.questionRef)}>
            <span>{t('answers.improvePrompt')}</span>
            <ArrowRight />
          </button>
          <button className="button button--bare" onClick={() => openAccordion('accordion-hints', props.hintRef)}>
            <span>{t('answers.readHints')}</span>
            <ArrowRight />
          </button>

          <FeedbackForm inputValue={props.inputValue} requestId={props.requestId} userId={props.userId} />
        </div>
      </div>
    </div>
  )
}
